'use client'

import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useStore } from 'jotai'
import { useCallback } from 'react'

import { openAssistant } from '@/features/order/chat/lib/open-assistant'
import type { ChatMessage } from '@/lib/types'
import {
  getAllResearchThreadTurns,
  type ResearchThreadSummary,
  type ResearchThreadTurn,
} from '@/service/research.api'
import { researchKeys } from '@/service/research.hook'
import {
  ASSISTANT_CARD_ID,
  ASSISTANT_SURFACE_ID,
  messagesAtomFamily,
  registerChatThreadAtom,
  researchConversationAtomFamily,
  serverThreadIndexAtom,
} from '@/state/chat'
import { markResearchThreadViewedAtom, type ResearchJob, researchJobsAtom } from '@/state/research'

type Store = ReturnType<typeof useStore>

const LOAD_FAILED_TEXT = "Couldn't load this conversation. Please try again."

/**
 * Turns a server thread's history into chat messages. A turn that carries a job becomes a
 * restored ResearchJob in place of its text: the poller fetches that job's report (or keeps
 * following it, if it's still running), exactly as for a run started in this session.
 *
 * `liveJobs`/`liveMessages` are what the thread already holds (a run restored after a reload):
 * a turn naming one of those jobs reuses its message as-is, and a live run the history doesn't
 * show yet is kept at the end, so nothing the poller is writing into goes missing.
 */
export function restoreThreadTurns(
  threadId: string,
  serverThreadId: string,
  turns: ResearchThreadTurn[],
  {
    liveJobs = [],
    liveMessages = [],
    now = Date.now(),
  }: {
    liveJobs?: ResearchJob[]
    liveMessages?: ChatMessage[]
    now?: number
  } = {}
): { messages: ChatMessage[]; jobs: ResearchJob[] } {
  const messages: ChatMessage[] = []
  const jobs: ResearchJob[] = []
  const liveByJobId = new Map(liveJobs.flatMap((job) => (job.jobId ? [[job.jobId, job]] : [])))
  const liveMessageById = new Map(liveMessages.map((message) => [message.id, message]))
  const matched = new Set<string>()
  let lastQuestion = ''

  for (const turn of turns) {
    const id = `history:${serverThreadId}:${turn.seq}`
    if (turn.role === 'user') {
      lastQuestion = turn.text
      if (turn.text) messages.push({ id, role: 'user', parts: [{ type: 'text', text: turn.text }] })
      continue
    }
    if (turn.jobId) {
      const live = liveByJobId.get(turn.jobId)
      const liveMessage = live && liveMessageById.get(live.assistantMessageId)
      if (live && liveMessage) {
        matched.add(live.id)
        messages.push(liveMessage)
        continue
      }
      const at = turn.createdAt || now
      messages.push({
        id,
        role: 'assistant',
        parts: [
          {
            type: 'component',
            componentType: 'RESEARCH_STATUS',
            data: { text: 'Loading the report…' },
          },
        ],
      })
      jobs.push({
        id: `history:${turn.jobId}`,
        jobId: turn.jobId,
        clientRequestId: `history:${turn.jobId}`,
        threadId,
        kind: turn.route === 'FOLLOWUP_ON_EXISTING' ? 'followup' : 'question',
        question: lastQuestion,
        assistantMessageId: id,
        // DELIVERED with no report is what the supervisor polls once for a visible thread; the
        // poll then corrects the phase if the run actually failed or is still going.
        phase: 'DELIVERED',
        events: [],
        report: null,
        error: null,
        startedAt: at,
        updatedAt: at,
        completedAt: at,
        notifiedAt: at,
        viewedAt: now,
        restored: true,
      })
      continue
    }
    // An assistant turn that only restates the question (a confirm card's stored copy) adds
    // nothing once the run's report follows it.
    if (turn.text && turn.text.trim() !== lastQuestion.trim()) {
      messages.push({ id, role: 'assistant', parts: [{ type: 'text', text: turn.text }] })
    }
  }

  for (const job of [...liveJobs].sort((a, b) => a.startedAt - b.startedAt)) {
    if (matched.has(job.id)) continue
    const index = liveMessages.findIndex((message) => message.id === job.assistantMessageId)
    if (index < 0) continue
    const question = liveMessages[index - 1]
    if (question?.role === 'user') messages.push(question)
    messages.push(liveMessages[index])
  }
  return { messages, jobs }
}

/** Fills a thread from GET /research/threads/{id}, once (see `historyLoaded`). */
function loadServerHistory(store: Store, queryClient: QueryClient, threadId: string) {
  const conversationAtom = researchConversationAtomFamily(threadId)
  const { serverThreadId, historyLoaded } = store.get(conversationAtom)
  if (!serverThreadId || historyLoaded !== false) return

  store.set(conversationAtom, (prev) => ({ ...prev, historyLoaded: true }))
  const messagesAtom = messagesAtomFamily(threadId)
  if (store.get(messagesAtom).length === 0) {
    store.set(messagesAtom, [
      {
        id: `history:${serverThreadId}:loading`,
        role: 'assistant',
        parts: [
          {
            type: 'component',
            componentType: 'RESEARCH_STATUS',
            data: { text: 'Loading conversation…' },
          },
        ],
      },
    ])
  }

  void queryClient
    .fetchQuery({
      queryKey: researchKeys.thread(serverThreadId),
      queryFn: () => getAllResearchThreadTurns(serverThreadId),
      staleTime: 0,
    })
    .then(
      ({ turns }) => {
        const liveJobs = Object.values(store.get(researchJobsAtom)).filter(
          (job) => job.threadId === threadId
        )
        const { messages, jobs } = restoreThreadTurns(threadId, serverThreadId, turns, {
          liveJobs,
          liveMessages: store.get(messagesAtom),
        })
        store.set(messagesAtom, messages)
        store.set(researchJobsAtom, (current) => ({
          ...current,
          ...Object.fromEntries(jobs.filter((job) => !current[job.id]).map((job) => [job.id, job])),
        }))
      },
      () => {
        store.set(conversationAtom, (prev) => ({ ...prev, historyLoaded: false }))
        store.set(messagesAtom, (messages) =>
          messages.some((message) => !message.id.endsWith(':loading'))
            ? messages
            : [
                {
                  id: `history:${serverThreadId}:error`,
                  role: 'assistant',
                  parts: [{ type: 'text', text: LOAD_FAILED_TEXT }],
                },
              ]
        )
      }
    )
}

/**
 * Opens a research thread in the assistant and marks its report read. A thread holding only
 * part of its server conversation (see `historyLoaded`) is filled in from the server.
 */
export function useOpenResearchThread() {
  const store = useStore()
  const queryClient = useQueryClient()

  return useCallback(
    (threadId: string) => {
      store.set(markResearchThreadViewedAtom, threadId)
      openAssistant(store, { thread: { kind: 'existing', threadId } })
      loadServerHistory(store, queryClient, threadId)
    },
    [queryClient, store]
  )
}

/**
 * Opens a thread from the server's history list. A thread already known in this session is
 * reused; otherwise a local thread is created bound to it, so the next message continues the
 * same server conversation.
 */
export function useOpenServerResearchThread() {
  const store = useStore()
  const openResearchThread = useOpenResearchThread()

  return useCallback(
    (summary: ResearchThreadSummary) => {
      const serverThreadId = summary.threadId
      const existing = store.get(serverThreadIndexAtom).get(serverThreadId)
      if (existing) {
        openResearchThread(existing)
        return
      }

      const threadId = `thread:server:${serverThreadId}`
      store.set(registerChatThreadAtom, {
        threadId,
        surfaceId: ASSISTANT_SURFACE_ID,
        cardId: ASSISTANT_CARD_ID,
        source: 'history',
        title: summary.title,
        createdAt: summary.createdAt,
        updatedAt: summary.updatedAt,
      })
      store.set(researchConversationAtomFamily(threadId), (prev) => ({
        ...prev,
        serverThreadId,
        historyLoaded: false,
      }))
      openResearchThread(threadId)
    },
    [openResearchThread, store]
  )
}
